// src/pages/finance-portal/tax-return-filings/TaxReturnFilings.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, Chip, Alert, Skeleton, MenuItem, Grid, Tabs, Tab,
    Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress,
    IconButton, Tooltip,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import axiosInstance from '../../../utils/axiosInstance';
import { useSnackbar } from 'notistack';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const STATUS_COLORS = {
    NOT_STARTED: { bg: '#f1f5f9', color: '#475569' },
    IN_PROGRESS: { bg: '#fef3c7', color: '#92400e' },
    READY_TO_FILE: { bg: '#dbeafe', color: '#1e40af' },
    FILED: { bg: '#dcfce7', color: '#166534' },
    ACCEPTED: { bg: '#d1fae5', color: '#065f46' },
    REJECTED: { bg: '#fecaca', color: '#991b1b' },
};

const STATUSES = [
    { value: '', label: 'All Statuses' },
    { value: 'NOT_STARTED', label: 'Not Started' },
    { value: 'IN_PROGRESS', label: 'In Progress' },
    { value: 'READY_TO_FILE', label: 'Ready to File' },
    { value: 'FILED', label: 'Filed' },
    { value: 'ACCEPTED', label: 'Accepted' },
    { value: 'REJECTED', label: 'Rejected' },
];

const FILING_FREQUENCIES = [
    { value: 'MONTHLY', label: 'Monthly' },
    { value: 'QUARTERLY', label: 'Quarterly' },
    { value: 'ANNUALLY', label: 'Annually' },
    { value: 'SEMI_ANNUALLY', label: 'Semi-Annually' },
];

const RETURN_TYPES = [
    { value: 'SALES_TAX', label: 'Sales Tax' },
    { value: 'USE_TAX', label: 'Use Tax' },
    { value: 'INCOME_TAX', label: 'Income Tax' },
    { value: 'FRANCHISE_TAX', label: 'Franchise Tax' },
    { value: 'PROPERTY_TAX', label: 'Property Tax' },
];

const US_STATES = [
    'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
    'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
    'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
    'VA','WA','WV','WI','WY','DC',
];

const headCellSx = {
    fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b',
    textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0',
};

const bodyCellSx = {
    fontFamily, fontSize: '0.85rem', color: '#1e293b', borderBottom: '1px solid #f1f5f9',
};

function formatDate(dateStr) {
    if (!dateStr) return '\u2014';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getStatusLabel(status) {
    if (!status) return '';
    return status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function TabPanel({ children, value, index }) {
    return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

export default function TaxReturnFilings() {
    const { enqueueSnackbar } = useSnackbar();
    const [activeTab, setActiveTab] = useState(0);
    const [error, setError] = useState(null);

    // Filings state
    const [filings, setFilings] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [isLoading, setIsLoading] = useState(true);

    // Filters
    const [filterState, setFilterState] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterYear, setFilterYear] = useState(new Date().getFullYear().toString());

    // Create dialog
    const [formOpen, setFormOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({
        state_code: '', return_type: 'SALES_TAX', filing_frequency: 'QUARTERLY',
        period_start: '', period_end: '', due_date: '',
    });

    // Mark Filed dialog
    const [markFiledOpen, setMarkFiledOpen] = useState(false);
    const [markFiledId, setMarkFiledId] = useState(null);
    const [confirmationNumber, setConfirmationNumber] = useState('');
    const [filing, setFiling] = useState(false);

    // Generate Sales Tax Return
    const [generateOpen, setGenerateOpen] = useState(false);
    const [generateData, setGenerateData] = useState({ state_code: '', period_start: '', period_end: '' });
    const [generating, setGenerating] = useState(false);

    // Calendar state
    const [calendarEntries, setCalendarEntries] = useState([]);
    const [calendarLoading, setCalendarLoading] = useState(false);

    const fetchFilings = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const params = { page: page + 1, per_page: rowsPerPage };
            if (filterState) params.state = filterState;
            if (filterStatus) params.status = filterStatus;
            if (filterYear) params.year = filterYear;
            const res = await axiosInstance.get('/api/finance/tax/return-filings', { params });
            if (res.data.success) {
                setFilings(res.data.data || []);
                setTotal(res.data.total || 0);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load filings');
        } finally {
            setIsLoading(false);
        }
    }, [page, rowsPerPage, filterState, filterStatus, filterYear]);

    const fetchCalendar = useCallback(async () => {
        setCalendarLoading(true);
        try {
            const params = {};
            if (filterYear) params.year = filterYear;
            const res = await axiosInstance.get('/api/finance/tax/return-filings/calendar', { params });
            if (res.data.success) {
                setCalendarEntries(res.data.data || []);
            }
        } catch { /* silent */ }
        finally { setCalendarLoading(false); }
    }, [filterYear]);

    useEffect(() => {
        if (activeTab === 0) fetchFilings();
        else fetchCalendar();
    }, [activeTab, fetchFilings, fetchCalendar]);

    const openCreateForm = () => {
        setFormData({
            state_code: '', return_type: 'SALES_TAX', filing_frequency: 'QUARTERLY',
            period_start: '', period_end: '', due_date: '',
        });
        setFormOpen(true);
    };

    const handleCreate = async () => {
        setSaving(true);
        try {
            await axiosInstance.post('/api/finance/tax/return-filings', formData);
            enqueueSnackbar('Filing created', { variant: 'success' });
            setFormOpen(false);
            fetchFilings();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to create filing', { variant: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const openMarkFiled = (f) => {
        setMarkFiledId(f.id);
        setConfirmationNumber('');
        setMarkFiledOpen(true);
    };

    const handleMarkFiled = async () => {
        setFiling(true);
        try {
            await axiosInstance.post(`/api/finance/tax/return-filings/${markFiledId}/mark-filed`, {
                confirmation_number: confirmationNumber,
            });
            enqueueSnackbar('Filing marked as filed', { variant: 'success' });
            setMarkFiledOpen(false);
            fetchFilings();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to mark as filed', { variant: 'error' });
        } finally {
            setFiling(false);
        }
    };

    const handleGenerateSalesTaxReturn = async () => {
        setGenerating(true);
        try {
            await axiosInstance.post('/api/finance/tax/return-filings/generate-sales-tax-return', generateData);
            enqueueSnackbar('Sales tax return generated', { variant: 'success' });
            setGenerateOpen(false);
            fetchFilings();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to generate return', { variant: 'error' });
        } finally {
            setGenerating(false);
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <DescriptionIcon sx={{ fontSize: 28, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>Tax Return Filings</Typography>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>Manage and track tax return filings</Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <Button
                            variant="contained"
                            startIcon={<ReceiptLongIcon />}
                            onClick={() => setGenerateOpen(true)}
                            sx={{
                                fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                                bgcolor: 'rgba(255,255,255,0.2)', color: 'white',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                            }}
                        >
                            Generate Sales Tax Return
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={openCreateForm}
                            sx={{
                                fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                                bgcolor: 'white', color: ACCENT,
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                            }}
                        >
                            Add Filing
                        </Button>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}

                {/* Year Filter */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                    <TextField
                        size="small" label="Year" type="number" value={filterYear}
                        onChange={e => { setFilterYear(e.target.value); setPage(0); }}
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                        sx={{ minWidth: 110 }}
                    />
                </Box>

                <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 1,
                    '& .MuiTab-root': { fontFamily, fontWeight: 600, textTransform: 'none', fontSize: '0.9rem' },
                    '& .Mui-selected': { color: ACCENT },
                    '& .MuiTabs-indicator': { bgcolor: ACCENT },
                }}>
                    <Tab label="Filings" />
                    <Tab label="Calendar" />
                </Tabs>

                {/* Filings Tab */}
                <TabPanel value={activeTab} index={0}>
                    {/* Filters */}
                    <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                        <TextField
                            size="small" select label="State" value={filterState}
                            onChange={e => { setFilterState(e.target.value); setPage(0); }}
                            InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                            InputLabelProps={{ sx: { fontFamily } }}
                            sx={{ minWidth: 100 }}
                        >
                            <MenuItem value="" sx={{ fontFamily }}>All States</MenuItem>
                            {US_STATES.map(st => <MenuItem key={st} value={st} sx={{ fontFamily }}>{st}</MenuItem>)}
                        </TextField>
                        <TextField
                            size="small" select label="Status" value={filterStatus}
                            onChange={e => { setFilterStatus(e.target.value); setPage(0); }}
                            InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                            InputLabelProps={{ sx: { fontFamily } }}
                            sx={{ minWidth: 160 }}
                        >
                            {STATUSES.map(s => <MenuItem key={s.value} value={s.value} sx={{ fontFamily }}>{s.label}</MenuItem>)}
                        </TextField>
                    </Box>

                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <TableContainer sx={{ overflowX: 'auto' }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        {['State', 'Return Type', 'Frequency', 'Period', 'Due Date', 'Status', 'Confirmation', 'Actions'].map(h => (
                                            <TableCell key={h} sx={headCellSx}>{h}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {isLoading ? (
                                        [1, 2, 3, 4, 5].map(i => (
                                            <TableRow key={i}>{[1, 2, 3, 4, 5, 6, 7, 8].map(j => <TableCell key={j}><Skeleton height={36} /></TableCell>)}</TableRow>
                                        ))
                                    ) : filings.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={8} sx={{ textAlign: 'center', py: 6 }}>
                                                <Typography sx={{ fontFamily, color: '#94a3b8' }}>No filings found</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : filings.map(f => (
                                        <TableRow key={f.id} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                            <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{f.state_code}</TableCell>
                                            <TableCell sx={bodyCellSx}>{getStatusLabel(f.return_type)}</TableCell>
                                            <TableCell sx={bodyCellSx}>{getStatusLabel(f.filing_frequency)}</TableCell>
                                            <TableCell sx={bodyCellSx}>
                                                {formatDate(f.period_start)} - {formatDate(f.period_end)}
                                            </TableCell>
                                            <TableCell sx={bodyCellSx}>{formatDate(f.due_date)}</TableCell>
                                            <TableCell sx={bodyCellSx}>
                                                <Chip label={getStatusLabel(f.status)} size="small"
                                                    sx={{
                                                        fontFamily, fontWeight: 600, fontSize: '0.72rem',
                                                        bgcolor: (STATUS_COLORS[f.status] || STATUS_COLORS.NOT_STARTED).bg,
                                                        color: (STATUS_COLORS[f.status] || STATUS_COLORS.NOT_STARTED).color,
                                                    }} />
                                            </TableCell>
                                            <TableCell sx={bodyCellSx}>{f.confirmation_number || '\u2014'}</TableCell>
                                            <TableCell sx={bodyCellSx}>
                                                {f.status !== 'FILED' && f.status !== 'ACCEPTED' && (
                                                    <Tooltip title="Mark as Filed">
                                                        <IconButton size="small" onClick={() => openMarkFiled(f)}>
                                                            <CheckCircleIcon sx={{ fontSize: 18, color: ACCENT }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div" count={total} page={page}
                            onPageChange={(_, p) => setPage(p)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                            rowsPerPageOptions={[10, 20, 50]}
                            sx={{ '& *': { fontFamily } }}
                        />
                    </Card>
                </TabPanel>

                {/* Calendar Tab */}
                <TabPanel value={activeTab} index={1}>
                    {calendarLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: ACCENT }} /></Box>
                    ) : calendarEntries.length === 0 ? (
                        <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', p: 4, textAlign: 'center' }}>
                            <Typography sx={{ fontFamily, color: '#94a3b8' }}>No upcoming due dates</Typography>
                        </Card>
                    ) : (
                        <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                            <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid #e2e8f0' }}>
                                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem' }}>Upcoming Due Dates</Typography>
                            </Box>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                            {['Due Date', 'State', 'Return Type', 'Period', 'Status'].map(h => (
                                                <TableCell key={h} sx={headCellSx}>{h}</TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {calendarEntries.map((entry, i) => {
                                            const isPast = entry.due_date && new Date(entry.due_date) < new Date();
                                            return (
                                                <TableRow key={i} sx={{
                                                    '&:hover': { bgcolor: '#f8fafc' },
                                                    bgcolor: isPast && entry.status !== 'FILED' && entry.status !== 'ACCEPTED' ? '#fef2f2' : 'inherit',
                                                }}>
                                                    <TableCell sx={{ ...bodyCellSx, fontWeight: 600, color: isPast && entry.status !== 'FILED' && entry.status !== 'ACCEPTED' ? '#dc2626' : '#1e293b' }}>
                                                        {formatDate(entry.due_date)}
                                                    </TableCell>
                                                    <TableCell sx={bodyCellSx}>{entry.state_code}</TableCell>
                                                    <TableCell sx={bodyCellSx}>{getStatusLabel(entry.return_type)}</TableCell>
                                                    <TableCell sx={bodyCellSx}>
                                                        {formatDate(entry.period_start)} - {formatDate(entry.period_end)}
                                                    </TableCell>
                                                    <TableCell sx={bodyCellSx}>
                                                        <Chip label={getStatusLabel(entry.status)} size="small"
                                                            sx={{
                                                                fontFamily, fontWeight: 600, fontSize: '0.72rem',
                                                                bgcolor: (STATUS_COLORS[entry.status] || STATUS_COLORS.NOT_STARTED).bg,
                                                                color: (STATUS_COLORS[entry.status] || STATUS_COLORS.NOT_STARTED).color,
                                                            }} />
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Card>
                    )}
                </TabPanel>
            </Box>

            {/* Create Filing Dialog */}
            <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Add Tax Return Filing</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth select label="State Code" required
                                value={formData.state_code}
                                onChange={e => setFormData(prev => ({ ...prev, state_code: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }}>
                                {US_STATES.map(st => <MenuItem key={st} value={st} sx={{ fontFamily }}>{st}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth select label="Return Type" required
                                value={formData.return_type}
                                onChange={e => setFormData(prev => ({ ...prev, return_type: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }}>
                                {RETURN_TYPES.map(t => <MenuItem key={t.value} value={t.value} sx={{ fontFamily }}>{t.label}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField size="small" fullWidth select label="Filing Frequency" required
                                value={formData.filing_frequency}
                                onChange={e => setFormData(prev => ({ ...prev, filing_frequency: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }}>
                                {FILING_FREQUENCIES.map(f => <MenuItem key={f.value} value={f.value} sx={{ fontFamily }}>{f.label}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth label="Period Start" type="date" required
                                value={formData.period_start}
                                onChange={e => setFormData(prev => ({ ...prev, period_start: e.target.value }))}
                                InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth label="Period End" type="date" required
                                value={formData.period_end}
                                onChange={e => setFormData(prev => ({ ...prev, period_end: e.target.value }))}
                                InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField size="small" fullWidth label="Due Date" type="date" required
                                value={formData.due_date}
                                onChange={e => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
                                InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setFormOpen(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button onClick={handleCreate} variant="contained" disabled={saving || !formData.state_code || !formData.due_date}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}>
                        {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Mark Filed Dialog */}
            <Dialog open={markFiledOpen} onClose={() => setMarkFiledOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Mark as Filed</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, color: '#475569', mb: 2, fontSize: '0.9rem' }}>
                        Enter the confirmation number from the filing authority.
                    </Typography>
                    <TextField size="small" fullWidth label="Confirmation Number" required
                        value={confirmationNumber}
                        onChange={e => setConfirmationNumber(e.target.value)}
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        InputLabelProps={{ sx: { fontFamily } }} />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setMarkFiledOpen(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button onClick={handleMarkFiled} variant="contained" disabled={filing || !confirmationNumber}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}>
                        {filing ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Confirm'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Generate Sales Tax Return Dialog */}
            <Dialog open={generateOpen} onClose={() => setGenerateOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Generate Sales Tax Return</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12}>
                            <TextField size="small" fullWidth select label="State Code" required
                                value={generateData.state_code}
                                onChange={e => setGenerateData(prev => ({ ...prev, state_code: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }}>
                                {US_STATES.map(st => <MenuItem key={st} value={st} sx={{ fontFamily }}>{st}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth label="Period Start" type="date" required
                                value={generateData.period_start}
                                onChange={e => setGenerateData(prev => ({ ...prev, period_start: e.target.value }))}
                                InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth label="Period End" type="date" required
                                value={generateData.period_end}
                                onChange={e => setGenerateData(prev => ({ ...prev, period_end: e.target.value }))}
                                InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setGenerateOpen(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button onClick={handleGenerateSalesTaxReturn} variant="contained"
                        disabled={generating || !generateData.state_code || !generateData.period_start || !generateData.period_end}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}>
                        {generating ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Generate'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
